/**
 * App — top-level router for Agentic Loan Audit.
 */

import { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute';
import { useAuthStore } from './auth/auth-store';
import { msalInstance } from './auth/msal';
import { apiRequest } from './api/client';
import { LoginPage } from './routes/login';
import { AuthCallbackPage } from './routes/auth-callback';
import { ProductPage } from './routes/marketing/ProductPage';
import { HowItWorksPage } from './routes/marketing/HowItWorksPage';
import { ResourcesPage } from './routes/marketing/ResourcesPage';
import { DashboardPage } from './routes/dashboard';
import { AccessManagerPage } from './routes/access-manager';
import { TeamPage } from './routes/team';
import { SettingsPage } from './routes/settings';
import { MastersPage } from './routes/masters';
import { AdminSystemPage } from './routes/admin-system';
import { AdminConfigPage } from './routes/admin-config';
import { NotificationsPage } from './routes/notifications';
import { NotificationPrefsPage } from './routes/notification-prefs';
import { LoanFilesPage } from './routes/loan-files';
import { LoanFileDetailPage } from './routes/loan-file-detail';
import { LoanFileUploadPage } from './routes/loan-file-upload';
import { ReviewQueuePage } from './routes/parsing/ReviewQueuePage';
import { ReviewItemDetailPage } from './routes/parsing/ReviewItemDetailPage';
import { FindingsPage } from './routes/validation/FindingsPage';
import { FindingDetailPage } from './routes/validation/FindingDetailPage';
import { PAGE_REGISTRY } from './page-registry';
import {
  DASHBOARD_VIEW,
  ACCESS_VIEW,
  MASTERS_VIEW,
  ADMIN_VIEW,
  ADMIN_EDIT_CONFIG,
  SETTINGS_VIEW,
  NOTIFICATIONS_VIEW,
  NOTIFICATIONS_MANAGE_PREFS,
  INGESTION_VIEW,
  INGESTION_UPLOAD,
  PARSING_VIEW,
  VALIDATION_VIEW,
} from '@loanaudit/shared';

export default function App() {
  const status = useAuthStore((s) => s.status);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const navigate = useNavigate();

  // OAuth callback detector. After MSAL.js completes the SPA flow with
  // Microsoft (handleRedirectPromise during initMsal), it stores tokens
  // in the cache and clears the URL fragment. So checking the URL after
  // MSAL has run gives us nothing — instead, we check whether MSAL has
  // an active account but our backend hasn't been told about it yet.
  //
  // Sequence:
  //  1. User clicks "Sign in with Microsoft" → loginRedirect to Microsoft
  //  2. Microsoft redirects back to / with #code=... in the URL
  //  3. main.tsx calls initMsal() before rendering App. initMsal calls
  //     handleRedirectPromise() which exchanges code → tokens, stores
  //     them, and clears window.location.hash
  //  4. App mounts. We see: MSAL has an account, but no session cookie
  //     yet (because backend hasn't been called). Trigger the callback.
  useEffect(() => {
    const accounts = msalInstance.getAllAccounts();
    const justAuthenticated = accounts.length > 0;
    const onLoginPage = window.location.hash === '#/login' || window.location.hash === '';
    if (justAuthenticated && onLoginPage && status !== 'authed') {
      navigate('/auth/callback', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => { void fetchMe(); }, [fetchMe]);

  useEffect(() => {
    if (status !== 'authed') return;
    apiRequest('/api/access/pages', {
      method: 'POST',
      body: { pages: PAGE_REGISTRY },
    }).catch(() => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Page registry sync failed');
      }
    });
  }, [status]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Public marketing pages (unauthenticated) */}
      <Route path="/product" element={<ProductPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/resources" element={<ResourcesPage />} />

      <Route path="/" element={
        <ProtectedRoute permission={DASHBOARD_VIEW}><DashboardPage /></ProtectedRoute>
      } />

      {/* Loan files */}
      <Route path="/loan-files" element={
        <ProtectedRoute permission={INGESTION_VIEW}><LoanFilesPage /></ProtectedRoute>
      } />
      <Route path="/loan-files/new" element={
        <ProtectedRoute permission={INGESTION_UPLOAD}><LoanFileUploadPage /></ProtectedRoute>
      } />
      <Route path="/loan-files/:id" element={
        <ProtectedRoute permission={INGESTION_VIEW}><LoanFileDetailPage /></ProtectedRoute>
      } />

      {/* Parsing — review queue (Sprint 5) */}
      <Route path="/parsing/review-queue" element={
        <ProtectedRoute permission={PARSING_VIEW}><ReviewQueuePage /></ProtectedRoute>
      } />
      <Route path="/parsing/review-queue/:reviewId" element={
        <ProtectedRoute permission={PARSING_VIEW}><ReviewItemDetailPage /></ProtectedRoute>
      } />

      {/* Validation — findings (Sprint 6) */}
      <Route path="/findings" element={
        <ProtectedRoute permission={VALIDATION_VIEW}><FindingsPage /></ProtectedRoute>
      } />
      <Route path="/findings/:findingId" element={
        <ProtectedRoute permission={VALIDATION_VIEW}><FindingDetailPage /></ProtectedRoute>
      } />

      {/* Admin */}
      <Route path="/admin/access-manager" element={
        <ProtectedRoute permission={ACCESS_VIEW}><AccessManagerPage /></ProtectedRoute>
      } />
      <Route path="/admin/team" element={
        <ProtectedRoute permission={ACCESS_VIEW}><TeamPage /></ProtectedRoute>
      } />
      <Route path="/admin/masters" element={
        <ProtectedRoute permission={MASTERS_VIEW}><MastersPage /></ProtectedRoute>
      } />
      <Route path="/admin/system" element={
        <ProtectedRoute permission={ADMIN_VIEW}><AdminSystemPage /></ProtectedRoute>
      } />
      <Route path="/admin/config" element={
        <ProtectedRoute permission={ADMIN_EDIT_CONFIG}><AdminConfigPage /></ProtectedRoute>
      } />

      {/* Notifications */}
      <Route path="/notifications" element={
        <ProtectedRoute permission={NOTIFICATIONS_VIEW}><NotificationsPage /></ProtectedRoute>
      } />
      <Route path="/settings/notifications" element={
        <ProtectedRoute permission={NOTIFICATIONS_MANAGE_PREFS}><NotificationPrefsPage /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute permission={SETTINGS_VIEW}><SettingsPage /></ProtectedRoute>
      } />

      <Route path="*" element={
        <ProtectedRoute permission={DASHBOARD_VIEW}><DashboardPage /></ProtectedRoute>
      } />
    </Routes>
  );
}
